import { useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { GoogleMap, LoadScript, MarkerClusterer, Marker, InfoWindow } from '@react-google-maps/api';
import { MapService } from '../../service';

import OfficeIcon from '../../img/office-icon-red.png';
import OfficeInfo from '../office-Info/office-info.component';
import ClusterIcon from '../../img/abs-marker.png';

const containerStyle = {
    width: '100%',
    height: '100%'
};

const markerStyles = [
    {
        url: ClusterIcon,
        textColor: '#fff',
        height: 30,
        width: 30
    }
];

const stylesArray = [
    {
        elementType: 'geometry',
        stylers: [
            {
                color: '#f5f5f5'
            }
        ]
    },
    {
        elementType: 'labels.icon',
        stylers: [
            {
                visibility: 'off'
            }
        ]
    },
    {
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#333333'
            }
        ]
    },
    {
        elementType: 'labels.text.stroke',
        stylers: [
            {
                color: '#f5f5f5'
            }
        ]
    },
    {
        featureType: 'administrative.land_parcel',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#bdbdbd'
            }
        ]
    },
    {
        featureType: 'poi',
        elementType: 'geometry',
        stylers: [
            {
                color: '#eeeeee'
            }
        ]
    },
    {
        featureType: 'poi',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#757575'
            }
        ]
    },
    {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [
            {
                color: '#e5e5e5'
            }
        ]
    },
    {
        featureType: 'poi.park',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#9e9e9e'
            }
        ]
    },
    {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [
            {
                color: '#ffffff'
            }
        ]
    },
    {
        featureType: 'road.arterial',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#757575'
            }
        ]
    },
    {
        featureType: 'road.highway',
        elementType: 'geometry',
        stylers: [
            {
                color: '#dadada'
            }
        ]
    },
    {
        featureType: 'road.highway',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#333333'
            }
        ]
    },
    {
        featureType: 'road.local',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#9e9e9e'
            }
        ]
    },
    {
        featureType: 'transit.line',
        elementType: 'geometry',
        stylers: [
            {
                color: '#e5e5e5'
            }
        ]
    },
    {
        featureType: 'transit.station',
        elementType: 'geometry',
        stylers: [
            {
                color: '#eeeeee'
            }
        ]
    },
    {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [
            {
                // visibility: 'simplified',
                color: '#c9c9c9'
            }
        ]
    },
    {
        featureType: 'water',
        elementType: 'labels.text.fill',
        stylers: [
            {
                color: '#9e9e9e'
            }
        ]
    }
];

const MapGoogle = ({ offices, triggerReset }) => {
    const [isLoading, setIsLoading] = useState(true);

    const mapInstance = useRef();
    const [mapKey, setMapKey] = useState();
    const [mapCenter, setMapCenter] = useState({ lat: 25, lng: 0 });
    const [mapZoom, setMapZoom] = useState(2);

    const [selectedOffice, setSelectedOffice] = useState();

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            await getApiKey();
            setIsLoading(false);
        })();
    }, []);

    useEffect(() => {
        // Center to location if there's only 1 office
        if (offices && offices.length === 1) {
            setMapCenter({
                lat: offices[0].Lat,
                lng: offices[0].Lng
            });
        }
    }, [offices]);

    useEffect(() => {
        resetMap();
    }, [triggerReset]);

    useEffect(() => {
        if (!selectedOffice) {
            resetMap();
        }
    }, [selectedOffice]);

    const resetMap = () => {
        setMapZoom(2);
        setMapCenter({ lat: 25, lng: 0 });
    };

    const getApiKey = async () => {
        const data = await MapService.getApi();

        if (!data) {
            console.log('Error: Starting Map.');
            return;
        }

        setMapKey(data);
    };

    const getIcon = () => {
        const markerIcon = {
            url: '',
            scaledSize: {
                width: 20,
                height: 20
            }
        };

        markerIcon.url = OfficeIcon;

        return markerIcon;
    };

    const handleSelectedOffice = (event, office) => {
        if (!office) return;

        // Move location to the center of the map
        setMapCenter({ lat: office.Lat, lng: office.Lng });

        setSelectedOffice(office);
    };

    return (
        <>
            {isLoading ? (
                <div style={{ padding: 8 }}>
                    <center>
                        <CircularProgress />
                    </center>
                </div>
            ) : (
                <>
                    <LoadScript googleMapsApiKey={mapKey}>
                        <GoogleMap
                            ref={(map) => {
                                mapInstance.current = map;
                            }}
                            mapContainerStyle={containerStyle}
                            center={mapCenter}
                            zoom={mapZoom}
                            options={{
                                mapTypeControl: false,
                                fullscreenControl: false,
                                streetViewControl: false,
                                styles: stylesArray,
                                minZoom: 1,
                                maxZoom: 15,
                                restriction: {
                                    latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
                                    strictBounds: true
                                }
                            }}
                            onZoomChanged={() => setMapZoom(1)}
                        >
                            <>
                                {offices && (
                                    <MarkerClusterer gridSize={25} styles={markerStyles}>
                                        {(clusterer) =>
                                            offices.map((office, index) => (
                                                <>
                                                    <Marker
                                                        key={`${office.Id}-${index}`}
                                                        position={{ lat: office.Lat, lng: office.Lng }}
                                                        clusterer={clusterer}
                                                        icon={getIcon(office)}
                                                        onClick={(event) => {
                                                            handleSelectedOffice(event, office);
                                                        }}
                                                    />
                                                </>
                                            ))
                                        }
                                    </MarkerClusterer>
                                )}
                                {selectedOffice && (
                                    <InfoWindow
                                        onCloseClick={() => {
                                            setSelectedOffice();
                                        }}
                                        position={{
                                            lat: selectedOffice.Lat,
                                            lng: selectedOffice.Lng
                                        }}
                                    >
                                        <OfficeInfo office={selectedOffice} style={{ margin: 0 }} />
                                    </InfoWindow>
                                )}
                            </>
                        </GoogleMap>
                    </LoadScript>
                </>
            )}
        </>
    );
};

export default MapGoogle;
